import { useCallback, useEffect, useMemo, useState } from "react";
import { X, ArrowLeft, ArrowRight, Zap, Pencil, Clock, PlayCircle, Hand } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const STORAGE_KEY = "tf_tour_seen_v1";

const stepIcons = [Zap, Pencil, Clock, PlayCircle, Hand];
const stepAccents = ["#FF3311", "#E5FF00", "#0014C7", "#E5FF00", "#00CCFF"];

export const WelcomeTour = () => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  const steps = useMemo(
    () => [
      { title: t("tour.step1.title"), body: t("tour.step1.body") },
      { title: t("tour.step2.title"), body: t("tour.step2.body") },
      { title: t("tour.step3.title"), body: t("tour.step3.body") },
      { title: t("tour.step4.title"), body: t("tour.step4.body") },
      { title: t("tour.step5.title"), body: t("tour.step5.body") },
    ],
    [t]
  );

  // On mount: show once per browser
  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) {
        // small delay so dashboard renders first
        const id = window.setTimeout(() => setOpen(true), 350);
        return () => window.clearTimeout(id);
      }
    } catch {}
  }, []);

  const close = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {}
    setOpen(false);
    setIndex(0);
  }, []);

  const next = useCallback(() => {
    setIndex((i) => (i < steps.length - 1 ? i + 1 : i));
  }, [steps.length]);

  const prev = useCallback(() => {
    setIndex((i) => (i > 0 ? i - 1 : 0));
  }, []);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        close();
      } else if (e.key === "ArrowRight" || e.key === "Enter") {
        if (index < steps.length - 1) next();
        else close();
      } else if (e.key === "ArrowLeft") {
        prev();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, index, next, prev, close, steps.length]);

  if (!open) return null;

  const Icon = stepIcons[index] || Zap;
  const accent = stepAccents[index] || "#E5FF00";
  const isLast = index === steps.length - 1;
  const step = steps[index];

  return (
    <div
      data-testid="welcome-tour"
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="tour-title"
        className="relative bg-white brutal-border brutal-shadow w-full max-w-lg p-6 md:p-8 font-mono"
      >
        {/* Close */}
        <button
          data-testid="tour-close"
          onClick={close}
          title={t("tour.skip")}
          className="absolute top-3 right-3 bg-white brutal-border w-9 h-9 flex items-center justify-center brutal-shadow-sm brutal-interactive"
        >
          <X className="w-5 h-5" strokeWidth={3} />
        </button>

        {/* Step indicator */}
        <div className="inline-flex items-center gap-2 brutal-border px-2 py-1 bg-black text-white text-[10px] uppercase tracking-widest">
          {t("tour.stepOf").replace("{n}", String(index + 1)).replace("{total}", String(steps.length))}
        </div>

        {/* Icon block */}
        <div className="mt-5 flex items-start gap-4">
          <div
            className="brutal-border w-14 h-14 shrink-0 flex items-center justify-center"
            style={{ backgroundColor: accent }}
          >
            <Icon className="w-7 h-7" strokeWidth={3} />
          </div>
          <div className="min-w-0">
            <h2
              id="tour-title"
              data-testid="tour-title"
              className="font-display uppercase text-2xl md:text-3xl leading-tight"
            >
              {step.title}
            </h2>
          </div>
        </div>

        {/* Body */}
        <p
          data-testid="tour-body"
          className="mt-5 text-sm md:text-base text-neutral-800 leading-relaxed"
        >
          {step.body}
        </p>

        {/* Dots */}
        <div className="mt-6 flex items-center gap-2">
          {steps.map((_, i) => (
            <span
              key={i}
              className={`w-3 h-3 brutal-border ${
                i === index ? "bg-black" : "bg-white"
              }`}
            />
          ))}
        </div>

        {/* Actions */}
        <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
          <button
            data-testid="tour-skip"
            onClick={close}
            className="bg-white brutal-border px-3 py-2 font-mono text-xs uppercase tracking-widest brutal-shadow-sm brutal-interactive"
          >
            {t("tour.skip")}
          </button>
          <div className="flex items-center gap-2">
            {index > 0 && (
              <button
                data-testid="tour-prev"
                onClick={prev}
                className="bg-white brutal-border px-3 py-2 font-mono text-xs uppercase tracking-widest brutal-shadow-sm brutal-interactive flex items-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" strokeWidth={3} />
                {t("tour.prev")}
              </button>
            )}
            {isLast ? (
              <button
                data-testid="tour-done"
                onClick={close}
                className="bg-[#FF3311] text-white brutal-border px-4 py-2 font-mono text-xs uppercase tracking-widest brutal-shadow-sm brutal-interactive"
              >
                {t("tour.done")}
              </button>
            ) : (
              <button
                data-testid="tour-next"
                onClick={next}
                className="bg-black text-white brutal-border px-4 py-2 font-mono text-xs uppercase tracking-widest brutal-shadow-sm brutal-interactive flex items-center gap-1"
              >
                {t("tour.next")}
                <ArrowRight className="w-4 h-4" strokeWidth={3} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeTour;
